import React from "react";

import type { ExcalidrawElement } from "@excalidraw/element/types";
import type { Theme } from "@excalidraw/element/types";

import { getLockIndicatorPosition } from "../utils/lockUtils";

import { LockedIcon } from "./icons";
import { Tooltip } from "./Tooltip";

interface LockIndicatorProps {
    element: ExcalidrawElement;
    zoom: number;
    theme: Theme;
    isSelected: boolean;
    isHovered?: boolean;
    onUnlock?: (elementId: string) => void;
}

export const LockIndicator: React.FC<LockIndicatorProps> = ({
    element,
    zoom,
    theme,
    isSelected,
    isHovered = false,
    onUnlock,
}) => {
    const [isInternalHovered, setIsInternalHovered] = React.useState(false);

    if (!element.locked || element.isDeleted) {
        return null;
    }

    const { x, y, size } = getLockIndicatorPosition(element, zoom);

    // Use internal hover state if external hover is not provided
    const effectiveHover = isHovered || isInternalHovered;

    const backgroundColor =
        theme === "dark"
            ? effectiveHover ? "rgba(0, 0, 0, 0.9)" : "rgba(0, 0, 0, 0.7)"
            : effectiveHover ? "rgba(255, 255, 255, 1)" : "rgba(255, 255, 255, 0.9)";
    const borderColor = isSelected ? "#1971c2" : effectiveHover ? "#6741d9" : "rgba(128, 128, 128, 0.3)";
    const iconColor = theme === "dark" ? "#ffffff" : "#000000";

    // Enhanced visual feedback on hover
    const hoverScale = effectiveHover ? 1.15 : 1.0;
    const hoverOpacity = 1.0;
    const hoverShadow = effectiveHover
        ? "0 4px 12px rgba(0, 0, 0, 0.4)"
        : "0 2px 6px rgba(0, 0, 0, 0.25)";

    const handleClick = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        if (onUnlock) {
            onUnlock(element.id);
        }
    };

    const lockIndicatorElement = (
        <div
            className="lock-indicator"
            style={{
                position: "absolute",
                left: x,
                top: y,
                width: size,
                height: size,
                backgroundColor,
                border: `2px solid ${borderColor}`,
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "auto",
                // Very high z-index to ensure it's always clickable, even with Hand tool
                zIndex: 999999,
                boxShadow: hoverShadow,
                color: iconColor,
                fontSize: `${size * 0.6}px`,
                transform: `scale(${hoverScale})`,
                opacity: hoverOpacity,
                transition: "all 0.15s ease-in-out",
                cursor: "pointer",
            }}
            title="Click to unlock this element"
            onClick={handleClick}
            onMouseEnter={() => setIsInternalHovered(true)}
            onMouseLeave={() => setIsInternalHovered(false)}
        >
            <div style={{
                width: `${size * 0.6}px`,
                height: `${size * 0.6}px`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}>
                {LockedIcon}
            </div>
        </div>
    );

    // Wrap with tooltip for enhanced hover feedback
    return (
        <Tooltip
            label={`🔒 Click to unlock this ${element.type} (or press Ctrl+Shift+L)`}
            long={true}
        >
            {lockIndicatorElement}
        </Tooltip>
    );
};

/**
 * Container component for rendering multiple lock indicators
 */
interface LockIndicatorsProps {
    elements: readonly ExcalidrawElement[];
    zoom: number;
    theme: Theme;
    selectedElementIds: Record<string, boolean>;
    hoveredElementIds: Record<string, boolean>;
    onUnlock?: (elementId: string) => void;
}

export const LockIndicators: React.FC<LockIndicatorsProps> = ({
    elements,
    zoom,
    theme,
    selectedElementIds,
    hoveredElementIds,
    onUnlock,
}) => {
    const lockedElements = elements.filter((el) => el.locked && !el.isDeleted);

    if (lockedElements.length === 0) {
        return null;
    }

    return (
        <div className="lock-indicators-container">
            {lockedElements.map((element) => (
                <LockIndicator
                    key={element.id}
                    element={element}
                    zoom={zoom}
                    theme={theme}
                    isSelected={!!selectedElementIds[element.id]}
                    isHovered={!!hoveredElementIds[element.id]}
                    onUnlock={onUnlock}
                />
            ))}
        </div>
    );
};
